import React, {useEffect, useState} from "react";
import {useStores} from "../../../../stores/useStores";
import {observer} from "mobx-react-lite";
import {useAsync} from "react-async";
import EventsCalendarTemplate from "../../../../components/templates/events/eventsCalendarTemplate";
import EventTemplatesModal from "../../../../components/organisms/eventTemplatesModal/eventTemplatesModal";
import {runInAction} from "mobx";
import EventTemplateCategory from "../../../../stores/domainStore/pages/models/eventTemplateCategory";
import {EventTemplateStatus} from "../../../../shared/enums/eventTemplateStatus";
import {EventCatalogueSortField} from "../../../../shared/enums/eventCatalogueSortField";
import {multiVillageKeyValue} from "../../../../shared/constants";
import {Navigation} from "../../../../utils/navigation";
import {QueryParams} from "../../../../shared/enums/queryParams";
import {useHistory} from "react-router";
import {RoutePaths} from "../../../../shared/routePaths";
import Skeleton from "react-loading-skeleton";
import "./eventCalendar.sass";

// Ignoring this unfortunately because eventsUiStore calls backendcaller before we can mock it
/* istanbul ignore file */

export interface IEventCalendarProps {
  switchToEditEvent: (eventId: string, occurrenceDate?: Date | null) => void;
  switchToViewEvent: (eventId: string, occurrenceDate?: Date | null) => void;
  switchToManageBooking: (
    eventId: string,
    occurrenceDate?: Date | null
  ) => void;
  currentCalendarView: string;
  setCurrentCalendarView: (selected: string) => void;
  switchToEditEventSeries: (eventId: string) => void;
  switchToViewEventSeries: (eventId: string) => void;
}

const EventCalendar: React.FC<IEventCalendarProps> = ({
  switchToEditEvent,
  switchToViewEvent,
  switchToManageBooking,
  currentCalendarView,
  setCurrentCalendarView,
  switchToEditEventSeries,
  switchToViewEventSeries
}) => {
  const [selectedTemplateCategories, setSelectedTemplateCategories] = useState<
    EventTemplateCategory[]
  >([]);
  const [hasData, setHasData] = useState(false);
  const {
    uiStore: {eventsUiStore, modalStore, eventCatalogueUiStore},
    uiStore,
    domainStore: {eventsDomainStore, eventCatalogueDomainStore}
  } = useStores();

  useEffect(() => {
    setSelectedTemplateCategories(
      eventCatalogueDomainStore.eventTemplateCategories
    );
  }, [eventCatalogueDomainStore.eventTemplateCategories]);

  const loadEventCategories = useAsync({
    deferFn: (args) => {
      const isFirstLoad = args[0];
      return eventCatalogueUiStore.loadEventCatalogue(
        EventTemplateStatus.Active,
        3,
        EventCatalogueSortField.lastUsed,
        isFirstLoad ? undefined : "",
        true,
        eventsUiStore.calendarTypeBySelectedCalendarName
      );
    },
    onResolve: () => {
      setSelectedTemplateCategories(
        eventCatalogueDomainStore.eventTemplateCategories
      );
      setHasData(true);
    },
    onReject: (error) => {
      modalStore.openErrorModal(error);
    }
  });

  const loadEventCategory = useAsync({
    deferFn: (args) => {
      return eventCatalogueUiStore.loadSelectedEventCategory(
        args[0],
        EventTemplateStatus.Active,
        eventsUiStore.calendarTypeBySelectedCalendarName
      );
    },
    onResolve: () => {
      setSelectedTemplateCategories([
        eventCatalogueDomainStore.eventTemplateCategory
      ]);
      setHasData(true);
    },
    onReject: (error) => {
      modalStore.openErrorModal(error);
    }
  });

  const loadEvents = useAsync({
    deferFn: (args) => eventsUiStore.navigateCalendarByDates(args[0], args[1]),
    onReject: (error) => modalStore.openErrorModal(error)
  });

  const loadEditableEvent = useAsync({
    deferFn: (args) => eventsDomainStore.loadEditableEvent(args[0]),
    onReject: (error) => modalStore.openErrorModal(error)
  });

  const doTemplateCategoryChanged = (id: string): void => {
    if (id === "") {
      loadEventCategories.run();
    } else {
      loadEventCategory.run(id);
    }
  };

  const history = useHistory();

  return (
    <div className="event-calendar">
      {eventsUiStore.isSwitchingVillage ? (
        <Skeleton className="placeholder" />
      ) : (
        <EventsCalendarTemplate
          isNavigating={eventsUiStore.isNavigating}
          isLoading={eventsUiStore.isLoading}
          initialDate={eventsUiStore.initialCalendarDate}
          loadEventDetail={loadEditableEvent.run}
          eventDetailIsLoading={loadEditableEvent.isLoading}
          eventDetail={eventsDomainStore.editableEvent}
          loadEvents={loadEvents.run}
          events={eventsDomainStore.eventsList}
          onEditEventClick={() => {
            eventsDomainStore.editableEvent.setIsDirty(false);
            switchToEditEvent(
              eventsDomainStore.editableEvent.id,
              eventsDomainStore.editableEvent.getOccurrenceDate
            );
          }}
          onViewEventClick={() => {
            eventsDomainStore.editableEvent.setIsDirty(false);
            switchToViewEvent(
              eventsDomainStore.editableEvent.id,
              eventsDomainStore.editableEvent.getOccurrenceDate
            );
          }}
          switchToEditEventSeries={() => {
            eventsDomainStore.editableEvent.setIsDirty(false);
            const eventSeriesId =
              eventsDomainStore.editableEvent.getEventSeriesId();
            eventsUiStore.setSelectedOccurrenceDate(
              eventsDomainStore.editableEvent.getOccurrenceDate
            );
            switchToEditEventSeries(eventSeriesId!);
          }}
          switchToViewEventSeries={() => {
            eventsDomainStore.editableEvent.setIsDirty(false);
            const eventSeriesId =
              eventsDomainStore.editableEvent.getEventSeriesId();
            switchToViewEventSeries(eventSeriesId!);
          }}
          createEventOnClick={(startDate?: Date, endDate?: Date) => {
            eventsDomainStore.resetEditableEvent();
            eventsDomainStore.startDate = startDate;
            runInAction(() => (eventCatalogueUiStore.isModalOpen = true));
            loadEventCategories.run(true);
          }}
          currentCalendarView={currentCalendarView}
          setCurrentCalendarView={setCurrentCalendarView}
          calendarSchedule={eventsUiStore.calendarSchedule}
          isMultiVillageOptionSelected={
            uiStore.selectableVillages.selectedItem?.value ===
            multiVillageKeyValue.value
          }
          switchToManageBooking={() => {
            switchToManageBooking(
              eventsDomainStore.editableEvent.id,
              eventsDomainStore.editableEvent.getOccurrenceDate
            );
          }}
        />
      )}
      <EventTemplatesModal
        isOpen={eventCatalogueUiStore.isModalOpen}
        isLoading={eventCatalogueUiStore.isLoading}
        hideFeature={true}
        toggle={() => {
          runInAction(() => (eventCatalogueUiStore.isModalOpen = false));
          setHasData(false);
          setSelectedTemplateCategories([]);
        }}
        eventTemplateCategories={
          eventCatalogueDomainStore.eventTemplateCategories
        }
        selectedTemplateCategories={selectedTemplateCategories}
        onTemplateCategoryChanged={doTemplateCategoryChanged}
        onUseTemplate={(id: string) => {
          Navigation.navigateWithParams(
            history,
            RoutePaths.CreateEvent,
            QueryParams.TemplateId,
            id
          );
          runInAction(() => (eventCatalogueUiStore.isModalOpen = false));
        }}
        hasData={hasData}
      />
    </div>
  );
};

export default observer(EventCalendar);
